import * as Yup from "yup";
import { runTextPipeline, validateFile } from "../../utils/contentSafety";

export const defaultValues = {
  // Business Details
  businessName: "",
  companyLegalName: "",
  
  // About Your Business
  businessBio: "",
  yearFounded: "",
  yearsOfExperience: "",
  teamSize: "",
  
  // Specialities
  specialities: [],
  
  // Top Services
  topServices: "",
  
  // Location & Areas
  primaryLocation: "",
  serviceAreas: [],
  
  // Operating Hours
  timeZone: "Australia/Melbourne",
  responseTime: "",
  
  // Weekly Schedule
  mondayOpen: false,
  mondayStart: "",
  mondayEnd: "",
  tuesdayOpen: false,
  tuesdayStart: "",
  tuesdayEnd: "",
  wednesdayOpen: false,
  wednesdayStart: "",
  wednesdayEnd: "",
  thursdayOpen: false,
  thursdayStart: "",
  thursdayEnd: "",
  fridayOpen: false,
  fridayStart: "",
  fridayEnd: "",
  saturdayOpen: false,
  saturdayStart: "",
  saturdayEnd: "",
  sundayOpen: false,
  sundayStart: "",
  sundayEnd: "",
  
  // Public Contact Info
  publicMobileNumber: "",
  publicEmailAddress: "",
  websiteUrl: "",
  
  // Accepted Payments
  paymentMethods: [],
  
  // Profile Media
  logoUpload: null,
  coverImageUpload: null,
  
  // Social Media
  facebookPage: "",
  instagram: "",
  linkedinCompany: "",
  twitter: "",
  tiktok: "",
  youtube: "",
  pinterest: "",
  otherSocialName: "",
  otherSocialUrl: ""
};

export const validationSchema = Yup.object({
  // Business Details Validation
  businessName: Yup.string()
    .required("Business name is required")
    .test("contentSafety", function (value) {
      if (!value || !String(value).trim()) return true;
      const result = runTextPipeline(value, "businessName");
      if (result.valid) return true;
      return this.createError({ message: result.message });
    }),
  companyLegalName: Yup.string()
    .test("contentSafety", function (value) {
      if (!value || !String(value).trim()) return true;
      const result = runTextPipeline(value, "businessName");
      if (result.valid) return true;
      return this.createError({ message: result.message });
    })
    .max(100, "Company legal name must be less than 100 characters"),
  
  // About Your Business Validation
  businessBio: Yup.string()
    .required("Business bio is required")
    .test("contentSafety", function (value) {
      if (!value || !String(value).trim()) return true;
      const result = runTextPipeline(value, "businessBio");
      if (result.valid) return true;
      return this.createError({ message: result.message });
    })
    .min(500, "Business bio must be at least 500 characters")
    .max(800, "Business bio must be less than 800 characters"),
  yearFounded: Yup.number()
    .min(1800, "Year founded must be after 1800")
    .max(new Date().getFullYear(), "Year founded cannot be in the future")
    .required("Year founded is required"),
  yearsOfExperience: Yup.number()
    .min(0, "Years of experience cannot be negative")
    .max(100, "Years of experience must be less than 100"),
  teamSize: Yup.string()
    .required("Team size is required"),
  
  // Specialities Validation
  specialities: Yup.array()
    .min(1, "At least one speciality is required"),
  
  // Top Services Validation
  topServices: Yup.string()
    .required("Top services is required")
    .test("contentSafety", function (value) {
      if (!value || !String(value).trim()) return true;
      const result = runTextPipeline(value, "topServices");
      if (result.valid) return true;
      return this.createError({ message: result.message });
    })
    .min(10, "Top services must be at least 10 characters")
    .max(1000, "Top services must be less than 1000 characters"),
  
  // Location & Areas Validation
  primaryLocation: Yup.string()
    .required("Primary location is required")
    .test("contentSafety", function (value) {
      if (!value || !String(value).trim()) return true;
      const result = runTextPipeline(value, "location");
      if (result.valid) return true;
      return this.createError({ message: result.message });
    }),
  serviceAreas: Yup.array()
    .min(1, "At least one service area is required"),
  
  // Operating Hours Validation
  timeZone: Yup.string()
    .required("Time zone is required"),
  responseTime: Yup.string()
    .required("Response time is required"),
  
  // Public Contact Info Validation
  publicMobileNumber: Yup.string()
    .matches(/^04\d{2} \d{3} \d{3}$/, 'Mobile number must be in format 04XX XXX XXX')
    .required("Public mobile number is required"),
  publicEmailAddress: Yup.string()
    .email("Invalid email format")
    .required("Public email address is required"),
  websiteUrl: Yup.string()
    .url("Invalid URL format")
    .required("Website URL is required"),
  
  // Accepted Payments Validation
  paymentMethods: Yup.array()
    .min(1, "At least one payment method is required"),
  
  // Profile Media Validation (content-safety config: 10MB max, JPG/PNG/WEBP/HEIC only, no GIF/SVG)
  logoUpload: Yup.mixed()
    .test("contentSafetyFile", function (value) {
      if (!value) return true;
      const result = validateFile(value, "image");
      if (result.valid) return true;
      return this.createError({ message: result.message });
    }),
  coverImageUpload: Yup.mixed()
    .test("contentSafetyFile", function (value) {
      if (!value) return true;
      const result = validateFile(value, "image");
      if (result.valid) return true;
      return this.createError({ message: result.message });
    }),
  
  // Social Media Validation
  facebookPage: Yup.string()
    .url("Invalid Facebook URL format"),
  instagram: Yup.string()
    .url("Invalid Instagram URL format"),
  linkedinCompany: Yup.string()
    .url("Invalid LinkedIn URL format"),
  twitter: Yup.string()
    .url("Invalid X (Twitter) URL format"),
  tiktok: Yup.string()
    .url("Invalid TikTok URL format"),
  youtube: Yup.string()
    .url("Invalid YouTube URL format"),
  pinterest: Yup.string()
    .url("Invalid Pinterest URL format"),
  otherSocialName: Yup.string()
    .test("contentSafety", function (value) {
      if (!value || !String(value).trim()) return true;
      const result = runTextPipeline(value, "supportSubject");
      if (result.valid) return true;
      return this.createError({ message: result.message });
    })
    .max(50, "Other social platform name must be less than 50 characters"),
  otherSocialUrl: Yup.string()
    .url("Invalid other social platform URL format")
});
